import { useEffect, useReducer } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { useTenantCode } from '../shared/tenant/TenantProvider.js';

import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';

import { useAccountMe } from '../features/account/accountApi.js';

import {

  BOTTOM_NAV_AUTH_INTENT_STATE_KEY,

  buildBottomNavHref,

  clearBottomNavMemoryAuthIntent,

  clearOrdersAuthGateIntent,

  isBottomNavAuthChromePath,

  resolveBottomNavActiveTab,

  setBottomNavMemoryAuthIntent,

  shouldHideBottomNav,

  type BottomNavTab,

} from './bottomNavActiveTab.js';

import {

  PLATFORM_BROWSE_PATH,

  PLATFORM_FAVORITES_PATH,

  PLATFORM_SHOPS_PATH,

} from '../features/shop/resolveShopHref.js';

import {

  useNavigationDestinations,

  resolveBottomNavPrimaryIds,

  buildBottomNavTrailingDestinationCandidates,

  filterDestinationsByIds,

  type NavIconComponent,

  type NavigationDestination,

} from '../features/navigation/customerNavigationModel.js';

import {

  HeartHandIcon,

  HistoryIcon,

  HomeIcon,

  MenuIcon,

  ScanIcon,

} from '../shared/ui/BottomNavIcons.js';

import { isGuestAuthPath } from '../features/auth/routing/isGuestAuthPath.js';

import { ORDERS_INTENT_PLATFORM_RECOVERY_PATH } from '../features/auth/routing/resolveGuestAuthRedirectTarget.js';

import { isGloballySignedInChrome } from '../features/hub/customerHubPaths.js';

import {
  clearPostAuthOrdersIntent,
  consumePostAuthOrdersIntent,
  peekInMemoryReturnTarget,
  peekPlatformReturnTarget,
  peekPostAuthOrdersIntent,
  peekReturnTarget,
  savePlatformReturnTarget,
  savePostAuthOrdersIntent,
} from '../shared/session/returnTarget.js';

import {

  clearMemoryMoreDestination,

  isMoreRowActive,

} from '../features/more/moreRouteUtils.js';

/** Max primary icons in the bar before the trailing overflow slot (Více or promoted). */

export const BOTTOM_NAV_MAX_PRIMARY = 3;



interface BottomNavItem {

  readonly tab: BottomNavTab;

  /** Visible label key (full form — no ellipsis). */

  readonly labelKey: string;

  /** Optional accessible name override when distinct from visible label. */

  readonly ariaLabelKey?: string;

  readonly testId: string;

  readonly Icon: NavIconComponent;

  readonly href?: string;

  /** True when this slot is a single promoted overflow destination (not Více). */

  readonly promoted?: boolean;

}



/**
 * Map shared NavigationDestinationId to BottomNavTab.
 * Explicit: NavigationDestinationId `'home'` ↔ BottomNavTab `'shop'` (Oblíbené chrome).
 */
export function destinationIdToTab(id: NavigationDestination['id']): BottomNavTab {
  if (id === 'browse') {
    return 'browse';
  }
  if (id === 'home') {
    return 'shop';
  }
  if (id === 'scan') {
    return 'scan';
  }
  if (id === 'orders') {
    return 'orders';
  }
  if (id === 'shops') {
    return 'shops';
  }
  return 'more';
}

/** Promoted overflow uses the same id→tab map (incl. shops if ever overflowed). */
function overflowIdToTab(id: NavigationDestination['id']): BottomNavTab {
  return destinationIdToTab(id);
}



function buildPromotedBottomNavItem(item: NavigationDestination): BottomNavItem {
  return {
    tab: overflowIdToTab(item.id),
    labelKey: item.labelKey,
    testId: item.testId,
    Icon: item.Icon,
    href: item.href,
    promoted: true,
  };
}

/** Canonical overflow promotion / sort order — same membership as BOTTOM_NAV_OVERFLOW_IDS. */
export const OVERFLOW_PROMOTION_ORDER: readonly NavigationDestination['id'][] = [
  'overview',
  'donate',
  'recurring',
  'my-donations',
  'find-order',
];

/** Deterministic overflow order before promote-vs-Více selection. */
export function orderOverflowDestinations(
  overflowDestinations: readonly NavigationDestination[],
): NavigationDestination[] {
  const rankById = new Map(
    OVERFLOW_PROMOTION_ORDER.map((id, index) => [id, index] as const),
  );
  return [...overflowDestinations].sort((a, b) => {
    const rankA = rankById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const rankB = rankById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return a.id.localeCompare(b.id);
  });
}

/** Fourth/fifth slot: promote lone overflow item; Více only when drawer has 2+ rows. */
export function buildBottomNavTrailingItem(
  overflowDestinations: readonly NavigationDestination[],
): BottomNavItem | null {
  // Normalize local ordering before any selection logic (including len === 1),
  // so promotion behavior stays deterministic even if upstream ordering changes.
  const orderedOverflow = orderOverflowDestinations(overflowDestinations);

  if (orderedOverflow.length === 0) {
    return null;
  }

  if (orderedOverflow.length === 1) {
    const only = orderedOverflow[0];
    if (only === undefined) {
      return null;
    }
    return buildPromotedBottomNavItem(only);
  }

  return {
    tab: 'more',
    labelKey: 'nav.more',
    testId: 'customer-bottom-nav-more',
    Icon: MenuIcon,
  };
}



function buildShopsDirectoryHref(_navTenantCode: string): string {

  // Obchody is always the cross-tenant public directory (all sales points).

  void _navTenantCode;

  return PLATFORM_SHOPS_PATH;

}



/** Platform orders URL — Wave D bare `/account/orders`. */

export function resolveOrdersNavHref(_navTenantCode?: string): string {

  void _navTenantCode;

  return '/account/orders';

}



/**

 * Signed-in Objednávky — platform `/account/orders`.

 * Never `/favorites` or `/sign-in` (those bounce guests / RequireGuestAuth).

 */

export function resolveSignedInOrdersRecoveryHref(): string {

  return ORDERS_INTENT_PLATFORM_RECOVERY_PATH;

}



interface BottomNavLabelProps {

  readonly labelKey: string;

}



/** Full single-line label — no truncate/ellipsis (Retail V1). */

function BottomNavLabel({ labelKey }: BottomNavLabelProps): JSX.Element {

  const { t } = useTranslation('customer');

  return (

    <span className="block w-full whitespace-nowrap text-center text-[11px] leading-none">{t(labelKey)}</span>

  );

}



interface BottomNavProps {

  readonly onMoreClick?: () => void;

  readonly isMoreActive?: boolean;

}



/**
 * MOB-01 geometry: equal flex columns + shared icon baseline.
 * - justify-start (not center) so active label metrics cannot shift icon tops
 * - font-medium for active+idle (semibold steals width / baseline at 320–430)
 * - px-1 keeps 4 slots inside customer-safe-area-x at 320 CSS px
 */
const NAV_LINK_BASE =

  'flex w-full min-w-0 min-h-11 overflow-hidden flex-col items-center justify-start gap-0.5 rounded-xl px-1 py-1.5 pt-2 text-[11px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]';

function buildBottomNavItemClassName(isActive: boolean): string {

  if (isActive) {

    return [

      NAV_LINK_BASE,

      'bg-[var(--color-accent)]',

      'text-[var(--color-accent-foreground)]',

      'shadow-[0_1px_2px_rgba(0,0,0,0.12)]',

    ].join(' ');

  }



  return [

    NAV_LINK_BASE,

    'text-[var(--color-on-surface-muted)]',

    'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',

  ].join(' ');

}



/**
 * Primary bottom-nav candidates (left → right).
 *
 * V1A / FR-01 HARD GATE (`showBrowse`): Nakupovat | Skenovat | Objednávky only —
 * never Oblíbené (`shop`) even when favorites/shops flags are on.
 * V1B (`!showBrowse` + `showFavorites`): Oblíbené | Skenovat | Objednávky
 * (tabs `shop | scan | orders` — Scan ON for product bar; no `scan?`).
 * Obchody append is gated at the BottomNav call-site (browse-on never appends).
 * Auth-gate flow still owns guest Objednávky access (browse is never gated).
 */
export function buildBottomNavPrimaryCandidates(
  primaryDestinations: readonly NavigationDestination[],
  shopHref?: string,
  showBrowse?: boolean,
  showScan?: boolean,
  showFavorites?: boolean,
): BottomNavItem[] {
  const browseOn = showBrowse === true;
  const scanOn = showScan === true;
  const favoritesPrimary = !browseOn && showFavorites === true;
  const favoritesHref =
    shopHref !== undefined && shopHref !== '' ? shopHref : PLATFORM_FAVORITES_PATH;

  const FALLBACK_PRIMARY: BottomNavItem[] = [
    ...(browseOn
      ? [
          {
            tab: 'browse' as const,
            labelKey: 'nav.browse',
            testId: 'customer-bottom-nav-browse',
            Icon: HomeIcon,
            href: PLATFORM_BROWSE_PATH,
          },
        ]
      : []),
    ...(favoritesPrimary
      ? [
          {
            tab: 'shop' as const,
            labelKey: 'nav.shop',
            testId: 'customer-bottom-nav-shop',
            Icon: HeartHandIcon,
            href: favoritesHref,
          },
        ]
      : []),
    ...(scanOn
      ? [
          {
            tab: 'scan' as const,
            labelKey: 'nav.scan',
            testId: 'customer-bottom-nav-scan',
            Icon: ScanIcon,
          },
        ]
      : []),
    { tab: 'orders', labelKey: 'nav.orders', testId: 'customer-bottom-nav-orders', Icon: HistoryIcon },
  ];

  const ORDER: BottomNavTab[] = [
    ...(browseOn ? ['browse' as const] : []),
    ...(favoritesPrimary ? ['shop' as const] : []),
    ...(scanOn ? ['scan' as const] : []),
    'orders',
  ];

  const mapped = new Map<BottomNavTab, BottomNavItem>();

  for (const dest of primaryDestinations) {
    const tab = destinationIdToTab(dest.id);
    mapped.set(tab, {
      tab,
      labelKey: dest.labelKey,
      testId: dest.testId,
      Icon: dest.Icon,
      href: dest.href,
    });
  }

  return ORDER.flatMap((tab) => {
    const found = mapped.get(tab);
    if (found !== undefined) {
      return [found];
    }
    const fallback = FALLBACK_PRIMARY.find((f) => f.tab === tab);
    if (fallback === undefined) {
      throw new Error(`Missing BottomNav fallback for tab: ${tab}`);
    }
    return [fallback];
  });
}

/** Obchody tab — appended after trailing when browse is off (V1B). */
function buildShopsBottomNavItem(
  allDestinations: readonly NavigationDestination[],
): BottomNavItem | null {
  const shopsDest = allDestinations.find((destination) => destination.id === 'shops');
  if (shopsDest === undefined) {
    return null;
  }
  return {
    tab: 'shops',
    labelKey: shopsDest.labelKey,
    testId: shopsDest.testId,
    Icon: shopsDest.Icon,
    href: shopsDest.href,
  };
}



export function BottomNav({ onMoreClick, isMoreActive = false }: BottomNavProps): JSX.Element | null {

  const { t } = useTranslation('customer');

  const tenantCode = useTenantCode();

  const location = useLocation();

  const navigate = useNavigate();

  const [, bumpAuthIntentEpoch] = useReducer((n: number) => n + 1, 0);

  const { session } = useCustomerSession();

  const me = useAccountMe(session !== null);

  const { destinations: allDestinations, showBrowse, showScan, showFavorites } =
    useNavigationDestinations();

  useEffect(() => {
    if (!isBottomNavAuthChromePath(location.pathname, tenantCode)) {
      clearOrdersAuthGateIntent();
    }
  }, [location.pathname, tenantCode]);

  const shopHref = allDestinations.find((d) => d.id === 'home')?.href ?? '';



  // After post-auth orders intent, resume platform orders once signed in.
  // Guest must keep the intent while soft-Navigate parks on /sign-in — consuming
  // here bounced guests back to /account/orders (blank shell vs RequireCustomerAuth).

  useEffect(() => {

    if (!peekPostAuthOrdersIntent()) {

      return;

    }

    if (session === null) {

      return;

    }

    consumePostAuthOrdersIntent();

    clearBottomNavMemoryAuthIntent();

    navigate(resolveSignedInOrdersRecoveryHref(), { replace: true });

  }, [navigate, session]);



  if (shouldHideBottomNav(location.pathname, tenantCode, {
    mustCompleteVerification: me.data?.mustCompleteVerification === true,
  })) {

    return null;

  }



  const onGuestAuthForm =

    isGuestAuthPath(location.pathname) ||

    isBottomNavAuthChromePath(location.pathname, tenantCode);

  const globallySignedIn = isGloballySignedInChrome({ session });

  const signedInChrome = globallySignedIn && !onGuestAuthForm;

  const tokenSignedIn = signedInChrome;

  const navTenantCode = tenantCode;



  const authIntentRaw = (location.state as Record<string, unknown> | null)?.[

    BOTTOM_NAV_AUTH_INTENT_STATE_KEY

  ];

  const authIntentTab =

    authIntentRaw === 'orders' || authIntentRaw === 'shop' || authIntentRaw === 'more'

      ? authIntentRaw

      : null;

  const returnTargetPath =

    peekInMemoryReturnTarget() ??

    peekReturnTarget({ tenantCode }) ??

    peekPlatformReturnTarget();

  void bumpAuthIntentEpoch;

  const activeTab = resolveBottomNavActiveTab(location.pathname, tenantCode, {

    authIntentTab,

    returnTargetPath,

    hasOrdersAuthIntent: peekPostAuthOrdersIntent(),

  });



  const primaryDestinations = filterDestinationsByIds(
    allDestinations,
    resolveBottomNavPrimaryIds(showBrowse, showScan, showFavorites),
  );

  const trailingDestinations = buildBottomNavTrailingDestinationCandidates(allDestinations, {
    showBrowse,
  });

  const candidates = buildBottomNavPrimaryCandidates(
    primaryDestinations,
    shopHref,
    showBrowse,
    showScan,
    showFavorites,
  );

  const primary = candidates.slice(0, BOTTOM_NAV_MAX_PRIMARY);

  const trailing = buildBottomNavTrailingItem(trailingDestinations);

  // FR-01 HARD GATE: browse-on never appends Obchody to the primary bar.
  const shopsItem =
    showBrowse === true ? null : buildShopsBottomNavItem(allDestinations);

  const items: BottomNavItem[] = [
    ...primary,
    ...(trailing === null ? [] : [trailing]),
    ...(shopsItem === null ? [] : [shopsItem]),
  ];



  return (

    <nav

      aria-label={t('nav.ariaLabel')}

      className="customer-safe-area-x fixed inset-x-0 bottom-0 z-40 isolate border-t border-[var(--color-border)] bg-[var(--color-surface)] pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] lg:hidden"

      data-testid="customer-bottom-nav"

    >

      <ul className="mx-auto flex w-full max-w-[var(--vcl-content-max-shell)] items-stretch justify-around gap-0.5">

        {items.map((item) => {

          let isActive = false;

          if (item.tab === 'more' && item.promoted !== true) {

            isActive = isMoreActive || activeTab === item.tab;

          } else if (item.promoted === true) {

            isActive = !isMoreActive && isMoreRowActive(location.pathname, item.href);

          } else {

            isActive = !isMoreActive && activeTab === item.tab;

          }

          const directHref =

            item.tab === 'shops' ? buildShopsDirectoryHref(navTenantCode) : item.href;

          const ordersHref = item.tab === 'orders' ? resolveOrdersNavHref() : null;

          const signInHref = '/sign-in';

          const defaultHref =

            item.tab === 'orders'

              ? (ordersHref ?? signInHref)

              : (directHref ?? buildBottomNavHref(item.tab, navTenantCode, { shopHref }));

          const isGuestAccessibleFavoritesPromoted =

            item.promoted === true &&

            item.tab === 'shop' &&

            !tokenSignedIn &&

            defaultHref.startsWith('/favorites');

          const needsAuthGate =

            (!tokenSignedIn && item.tab === 'orders') ||

            (item.promoted === true && !signedInChrome && !isGuestAccessibleFavoritesPromoted);

          let href = defaultHref;

          if (needsAuthGate) {

            href = signInHref;

          }

          const itemClassName = buildBottomNavItemClassName(isActive);

          const authGateState =

            needsAuthGate &&

            (item.tab === 'orders' || item.tab === 'shop' || item.promoted === true)

              ? {

                  [BOTTOM_NAV_AUTH_INTENT_STATE_KEY]:

                    item.promoted === true ? 'more' : item.tab,

                }

              : undefined;



          const accessibleName =

            item.ariaLabelKey !== undefined ? t(item.ariaLabelKey) : undefined;



          if (item.tab === 'more' && item.promoted !== true && onMoreClick !== undefined) {

            return (

              <li key={item.tab} className="flex min-w-0 flex-1">

                <button

                  type="button"

                  data-testid={item.testId}

                  aria-current={isActive ? 'page' : undefined}

                  aria-label={accessibleName}

                  title={accessibleName}

                  className={itemClassName}

                  onClick={onMoreClick}

                >

                  <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
                    <item.Icon className="h-5 w-5 shrink-0" />
                  </span>

                  <BottomNavLabel labelKey={item.labelKey} />

                </button>

              </li>

            );

          }



          return (

            <li key={item.tab} className="flex min-w-0 flex-1">

              <Link

                to={href}

                state={authGateState}

                data-testid={item.testId}

                aria-current={isActive ? 'page' : undefined}

                aria-label={accessibleName}

                title={accessibleName}

                className={itemClassName}

                onClick={() => {

                  if (!needsAuthGate) {

                    clearOrdersAuthGateIntent();

                    clearMemoryMoreDestination();

                    return;

                  }

                  clearMemoryMoreDestination();

                  if (item.promoted === true && item.href !== undefined) {

                    clearPostAuthOrdersIntent();

                    setBottomNavMemoryAuthIntent('more');

                    bumpAuthIntentEpoch();

                    savePlatformReturnTarget(item.href);

                    if (

                      location.pathname === href &&

                      authGateState !== undefined

                    ) {

                      navigate(href, { replace: true, state: authGateState });

                    }

                    return;

                  }

                  if (item.tab === 'orders') {

                    // Peer RequireCustomerAuth orders park: durable platform path + intent.

                    savePlatformReturnTarget(resolveOrdersNavHref());

                    savePostAuthOrdersIntent();

                    setBottomNavMemoryAuthIntent('orders');

                    bumpAuthIntentEpoch();

                  } else if (item.tab === 'shop') {

                    clearPostAuthOrdersIntent();

                    setBottomNavMemoryAuthIntent('shop');

                    bumpAuthIntentEpoch();

                    const platformTarget =

                      defaultHref === PLATFORM_SHOPS_PATH ||

                      defaultHref.startsWith('/shops') ||

                      defaultHref === '/favorites' ||

                      defaultHref.startsWith('/favorites') ||

                      defaultHref.startsWith('/account/')

                        ? defaultHref

                        : `${location.pathname}${location.search}`;

                    savePlatformReturnTarget(platformTarget);

                  }

                  if (

                    location.pathname === href &&

                    authGateState !== undefined &&

                    (item.tab === 'orders' || item.tab === 'shop')

                  ) {

                    navigate(href, { replace: true, state: authGateState });

                  }

                }}

              >

                <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
                  <item.Icon className="h-5 w-5 shrink-0" />
                </span>

                <BottomNavLabel labelKey={item.labelKey} />

              </Link>

            </li>

          );

        })}

      </ul>

    </nav>

  );

}


